import React, { useState } from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import {
  Code,
  GitPullRequest,
  CheckCircle2,
  Download,
  FileText,
  ExternalLink,
  GitMerge,
  ShieldCheck,
  Check,
} from 'lucide-react';

export const PatchViewer: React.FC = () => {
  const { activeIncident } = useAutoFix();
  const [prCreated, setPrCreated] = useState(false);
  const [prMerged, setPrMerged] = useState(false);

  if (!activeIncident || !activeIncident.patch) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: 32,
          textAlign: 'center',
          color: 'var(--text-3)',
        }}
      >
        <FileText size={36} style={{ marginBottom: 12, color: 'var(--text-3)' }} />
        <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>No Active Patch</h4>
        <p style={{ fontSize: 12, maxWidth: 300, marginTop: 4 }}>
          When an incident occurs, AutoFix synthesizes an AST code hotpatch and displays the unified diff here.
        </p>
      </div>
    );
  }

  const { patch } = activeIncident;

  const downloadDiff = () => {
    const blob = new Blob([patch.diff], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `autofix-${patch.id}.patch`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreatePR = () => {
    setPrCreated(true);
  };

  const handleMergePR = () => {
    setPrMerged(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Patch Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <GitPullRequest size={15} style={{ color: 'var(--accent)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
              {patch.id} · {patch.targetModule}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
            Applied into active memory · Zero page reload required
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {patch.verified && (
            <span
              className="badge badge-green"
              style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <CheckCircle2 size={11} /> VERIFIED BY WEBMCP
            </span>
          )}

          <button className="btn btn-sm" onClick={downloadDiff} title="Download .patch unified diff">
            <Download size={11} /> .patch
          </button>

          {!prCreated ? (
            <button
              className="btn btn-sm btn-primary"
              onClick={handleCreatePR}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}
            >
              <GitPullRequest size={12} /> Create GitHub PR
            </button>
          ) : (
            <span className="badge badge-green" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={12} /> PR #104 Generated
            </span>
          )}
        </div>
      </div>

      {/* GitHub Pull Request Live Simulation Card */}
      {prCreated && (
        <div
          style={{
            background: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: 8,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  background: prMerged ? '#8957e5' : '#238636',
                  color: '#fff',
                  borderRadius: 20,
                  padding: '3px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {prMerged ? <GitMerge size={12} /> : <GitPullRequest size={12} />}
                {prMerged ? 'Merged' : 'Open'}
              </div>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#c9d1d9' }}>
                  fix({activeIncident.bugId}): auto-heal runtime fault via WebMCP AST mutation
                </span>
                <span style={{ fontSize: 12, color: '#8b949e', marginLeft: 6 }}>#104</span>
              </div>
            </div>

            {!prMerged ? (
              <button
                className="btn btn-sm"
                onClick={handleMergePR}
                style={{
                  background: '#238636',
                  color: '#fff',
                  border: 'none',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 10px',
                }}
              >
                <GitMerge size={12} /> Merge into main
              </button>
            ) : (
              <span style={{ fontSize: 11, color: '#a371f7', fontWeight: 600 }}>
                ✓ Merged to branch <code>main</code>
              </span>
            )}
          </div>

          <div style={{ fontSize: 11, color: '#8b949e', lineHeight: 1.4 }}>
            Branch <code>autofix/{patch.id}</code> into <code>main</code> · Verified by WebMCP Autonomous Engine
          </div>

          <div
            style={{
              background: '#161b22',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 11,
              color: '#c9d1d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid #30363d',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ShieldCheck size={14} style={{ color: '#3fb950' }} />
              <span>CI Pipeline: 3 checks passing (AST Syntax, Runtime Regression, Clamped Bounds)</span>
            </div>
            <span style={{ color: '#3fb950', fontWeight: 600 }}>Passed</span>
          </div>
        </div>
      )}

      {/* Code Diff Display */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          background: '#090d16',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            background: 'rgba(255,255,255,0.03)',
            borderBottom: '1px solid var(--border)',
            fontSize: 11,
            color: 'var(--text-2)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span>Unified Git Diff</span>
          <span>Target: {patch.targetModule}</span>
        </div>

        <div
          style={{
            padding: 12,
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            lineHeight: 1.6,
            overflowX: 'auto',
            maxHeight: 380,
            overflowY: 'auto',
          }}
        >
          {patch.diff.split('\n').map((line, idx) => {
            const isAdd = line.startsWith('+');
            const isRemove = line.startsWith('-');
            const isHeader = line.startsWith('---') || line.startsWith('+++');

            let color = 'var(--text-2)';
            let background = 'transparent';

            if (isAdd) {
              color = 'var(--green)';
              background = 'rgba(16,185,129,0.08)';
            } else if (isRemove) {
              color = 'var(--red)';
              background = 'rgba(239,68,68,0.08)';
            } else if (isHeader) {
              color = 'var(--accent)';
            }

            return (
              <div
                key={idx}
                style={{
                  color,
                  background,
                  padding: '1px 6px',
                  borderRadius: 2,
                  whiteSpace: 'pre',
                }}
              >
                {line}
              </div>
            );
          })}
        </div>
      </div>

      {/* Verification Summary Card */}
      {patch.verificationResult && (
        <div
          style={{
            background: 'var(--green-soft)',
            border: '1px solid var(--green)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={16} style={{ color: 'var(--green)' }} />
            <span style={{ color: 'var(--text-1)' }}>
              <strong>Regression Assertion:</strong> {patch.verificationResult}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
